"use client"

import { useEffect, useState } from "react"
import dynamic from "next/dynamic"

/**
 * Bottom-of-the-app UI surfaces and beacons that aren't part of the
 * first paint:
 *
 *   - GoogleOneTap   — auth popup, only meaningful for unauthenticated
 *                      visitors and only after they've engaged
 *   - SupportWidget  — fixed bottom-right, never above the fold
 *   - CookieConsent  — fixed bottom banner, only relevant on first
 *                      visit and even then can wait a beat
 *   - Analytics      — Vercel Analytics beacon. Doesn't render
 *                      anything visible; firing late costs nothing
 *                      observable while keeping its JS out of the
 *                      initial bundle.
 *
 * Loading them eagerly with the rest of the layout pushed FCP / LCP
 * out by ~300-600ms (each one ships its own JS + runs its own effects
 * during initial render). Now they:
 *
 *   1. Aren't in the initial JS bundle (next/dynamic with ssr:false
 *      code-splits them into separate chunks).
 *   2. Don't render until the browser hits idle or a 1.5 s ceiling,
 *      whichever comes first — gives the critical paint room to
 *      breathe before any of these widgets compete for the main
 *      thread.
 *
 * They're all position-fixed at the edges of the viewport (or render
 * nothing), so mounting them late doesn't shift any existing layout
 * (CLS stays at 0).
 */
const GoogleOneTap  = dynamic(() => import("@/components/auth/GoogleOneTap"),                    { ssr: false })
const SupportWidget = dynamic(() => import("@/components/support-widget").then(m => ({ default: m.SupportWidget })), { ssr: false })
const CookieConsent = dynamic(() => import("@/components/consent/CookieConsent").then(m => ({ default: m.CookieConsent })), { ssr: false })
const Analytics     = dynamic(() => import("@vercel/analytics/next").then(m => ({ default: m.Analytics })), { ssr: false })

export default function DeferredShells() {
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    const ric = (window as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback
    if (typeof ric === "function") {
      const id = ric(() => setMounted(true), { timeout: 1500 })
      return () => {
        const cic = (window as { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback
        if (typeof cic === "function") cic(id)
      }
    }
    const id = setTimeout(() => setMounted(true), 1000)
    return () => clearTimeout(id)
  }, [])

  if (!mounted) return null

  return (
    <>
      <GoogleOneTap />
      <SupportWidget />
      <CookieConsent />
      <Analytics />
    </>
  )
}
